import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CalendarEventsProvider } from '../contexts/CalendarEventsContext';
import { PrivateRoute } from './PrivateRoute';
import { AppRoute } from './AppRoute';
import ArchivedProperties from './ArchivedProperties';
import ArchivedTenants from './ArchivedTenants';
import AccountManagement from './auth/AccountManagement';
import { usePageTracking } from '../hooks/useAnalytics';

const PropertyLegacyRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/properties/${id}`} replace />;
};

const lazy = (importFn) => React.lazy(importFn);

const DashboardV2 = lazy(() => import('./DashboardV2'));
const Properties = lazy(() => import('./Properties'));
const PropertyDetailsV2 = lazy(() => import('./PropertyDetailsV2'));
const AddProperty = lazy(() => import('./AddProperty'));
const EditProperty = lazy(() => import('./EditProperty'));
const Tenants = lazy(() => import('./Tenants'));
const AddTenant = lazy(() => import('./AddTenant'));
const TenantDetails = lazy(() => import('./TenantDetails'));
const EditTenant = lazy(() => import('./EditTenant'));
const Maintenance = lazy(() => import('./Maintenance'));
const MaintenanceDetails = lazy(() => import('./MaintenanceDetails'));
const InspectionsCalendar = lazy(() => import('./InspectionsCalendar'));
const Finance = lazy(() => import('./Finance'));
const Reports = lazy(() => import('./Reports'));
const Settings = lazy(() => import('./Settings'));
const Profile = lazy(() => import('./Profile'));
const Landlords = lazy(() => import('./Landlords'));
const LandlordDetails = lazy(() => import('./LandlordDetails'));
const AddLandlord = lazy(() => import('./AddLandlord'));
const Notifications = lazy(() => import('./Notifications'));
const Payments = lazy(() => import('./Payments'));
const Messages = lazy(() => import('./Messages'));
const Links = lazy(() => import('./Links'));
const CompanyDocuments = lazy(() => import('./CompanyDocuments'));
const EditPayment = lazy(() => import('./EditPayment'));
const Archive = lazy(() => import('./Archive'));
const Compliance = lazy(() => import('./Compliance'));
const Contractors = lazy(() => import('./Contractors'));
const ContractorMessages = lazy(() => import('./ContractorMessages'));
const Broadcast = lazy(() => import('./Broadcast'));
const Contacts = lazy(() => import('./Contacts'));
const Enquiries = lazy(() => import('./Enquiries'));
const Sales = lazy(() => import('./Sales'));
const SalesListingDetail = lazy(() => import('./sales/SalesListingDetail'));
const AddEnquiry = lazy(() => import('./AddEnquiry'));
const EditEnquiry = lazy(() => import('./EditEnquiry'));
const Bookings = lazy(() => import('./Bookings'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-secondary">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="mt-4 text-text-secondary">Loading...</span>
    </div>
  </div>
);

/** ponytail: shrink route boilerplate — layout + RBAC (+ optional feature flag) */
const route = (Component, { pageName, hideLayout, featureFlag, requiredFeature } = {}) => (
  <AppRoute
    pageName={pageName}
    hideLayout={hideLayout}
    featureFlag={featureFlag}
    requiredFeature={requiredFeature}
  >
    <Component />
  </AppRoute>
);

const layoutOnly = (Component) => (
  <PrivateRoute>
    <Component />
  </PrivateRoute>
);

const AuthenticatedApp = () => {
  usePageTracking();

  return (
    <CalendarEventsProvider>
      <Suspense fallback={<LoadingFallback />}>
        <div className="min-h-screen w-full bg-background text-text-primary">
          <Routes>
            <Route path="/" element={route(DashboardV2, { pageName: 'dashboard', hideLayout: true })} />
            <Route path="/dashboard" element={route(DashboardV2, { pageName: 'dashboard', hideLayout: true })} />
            <Route path="/dashboard-new" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard-v2" element={<Navigate to="/dashboard" replace />} />

            <Route path="/properties" element={route(Properties, { pageName: 'properties' })} />
            <Route path="/properties/add" element={route(AddProperty, { pageName: 'properties' })} />
            <Route path="/properties/:id" element={route(PropertyDetailsV2, { pageName: 'properties' })} />
            <Route path="/properties/:id/legacy" element={<PropertyLegacyRedirect />} />
            <Route path="/edit-property/:id" element={route(EditProperty, { pageName: 'properties' })} />
            <Route path="/archived-properties" element={route(ArchivedProperties, { pageName: 'properties' })} />
            <Route path="/properties/archived" element={<Navigate to="/archived-properties" replace />} />

            <Route path="/sales/listing/:listingId" element={route(SalesListingDetail, { pageName: 'sales' })} />
            <Route path="/sales" element={route(Sales, { pageName: 'sales' })} />

            <Route path="/tenants" element={route(Tenants, { pageName: 'tenants' })} />
            <Route path="/tenants/add" element={route(AddTenant, { pageName: 'tenants' })} />
            <Route path="/tenants/:id" element={route(TenantDetails, { pageName: 'tenants' })} />
            <Route path="/edit-tenant/:id" element={route(EditTenant, { pageName: 'tenants' })} />
            <Route path="/archived-tenants" element={route(ArchivedTenants, { pageName: 'tenants' })} />
            <Route path="/tenants/archived" element={<Navigate to="/archived-tenants" replace />} />

            <Route path="/maintenance" element={route(Maintenance, { pageName: 'maintenance' })} />
            <Route path="/maintenance/:id" element={route(MaintenanceDetails, { pageName: 'maintenance' })} />

            <Route
              path="/inspections"
              element={route(InspectionsCalendar, { pageName: 'inspections', featureFlag: 'inspections' })}
            />

            <Route path="/finance" element={route(Finance, { pageName: 'finance', featureFlag: 'finance_module' })} />
            <Route path="/payments" element={route(Payments, { pageName: 'finance', featureFlag: 'finance_module' })} />
            <Route path="/edit-payment/:id" element={route(EditPayment, { pageName: 'finance', featureFlag: 'finance_module' })} />
            <Route path="/reports" element={route(Reports, { pageName: 'reports', featureFlag: 'finance_module' })} />

            <Route path="/contractors" element={route(Contractors, { pageName: 'contractors' })} />
            <Route path="/archive" element={route(Archive, { pageName: 'archive' })} />
            <Route path="/settings" element={route(Settings, { pageName: 'settings' })} />
            <Route path="/profile" element={layoutOnly(Profile)} />

            <Route path="/landlords" element={route(Landlords, { pageName: 'landlords' })} />
            <Route path="/landlords/:id" element={route(LandlordDetails, { pageName: 'landlords' })} />
            <Route path="/add-landlord" element={route(AddLandlord, { pageName: 'landlords' })} />

            <Route path="/notifications" element={layoutOnly(Notifications)} />
            <Route path="/compliance" element={route(Compliance, { pageName: 'properties' })} />
            <Route path="/messages" element={route(Messages, { pageName: 'messages' })} />
            <Route path="/contractor-messages" element={route(ContractorMessages, { pageName: 'contractors' })} />
            <Route path="/links" element={route(Links, { pageName: 'links' })} />
            <Route path="/calendar" element={<Navigate to="/inspections" replace />} />
            <Route path="/contacts" element={route(Contacts, { pageName: 'properties' })} />
            <Route path="/company-documents" element={route(CompanyDocuments, { pageName: 'companyDocuments' })} />

            <Route path="/enquiries" element={route(Enquiries, { pageName: 'enquiries' })} />
            <Route path="/enquiries/add" element={route(AddEnquiry, { pageName: 'enquiries' })} />
            <Route path="/enquiries/:id/edit" element={route(EditEnquiry, { pageName: 'enquiries' })} />
            <Route path="/bookings" element={route(Bookings, { pageName: 'enquiries' })} />

            <Route
              path="/broadcast"
              element={route(Broadcast, { pageName: 'broadcast', requiredFeature: 'sendBroadcasts' })}
            />

            <Route path="/account" element={layoutOnly(AccountManagement)} />
            <Route path="/account-management" element={<Navigate to="/account" replace />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Suspense>
    </CalendarEventsProvider>
  );
};

export default AuthenticatedApp;
